<template>
  <v-container class="ma-0 pa-0" fluid>
    <v-data-table
      :headers="headers"
      :height="tableSize"
      :items-per-page="pageSize"
      :items="batches"
      :search="search"
      :loading="loading"
      @click:row="viewReport"
      dense
      fixed-header
      hide-default-footer
      item-key="id"
      style="cursor: pointer">
      <template #top>
          <v-toolbar flat color="toolbar">
            <v-row md="3" lg="3" xl="2" class="pl-0 pr-2">
              <v-col>
                <v-autocomplete v-if="!isTprProRetailUser"
                :items="userAdGroups"
                background-color="input"
                class="mt-7 ma-0"
                dense
                item-text="name"
                item-id="id"
                label="Ad Group"
                :menu-props="{ maxHeight: 210 }"
                outlined
                return-object
                @change="getBatches"
                v-model="adGroup">
                  <template #append>
                    <v-progress-circular
                      v-if="userGroupsLoading"
                      indeterminate
                      size="25"
                      color="primary" />
                  </template>
                </v-autocomplete>
                <v-row class="pl-1 pr-4" v-else-if="useStoreParties">
                  <StorePartiesDropdown
                    :userStoreParties="userStoreParties"
                    @updateStoreParty="updateStoreParty">
                  </StorePartiesDropdown>
                  <v-btn
                    v-if="storeParty && storeParty.party_type === 'TPR_STORE_GROUP'"
                    height="40"
                    depressed
                    color="primary"
                    style="border-top-left-radius: 0px; border-bottom-left-radius:0px; border: 1px solid #9e9e9e; border-left: 0px;"
                    @click="storeGroupModal = true">
                    <v-icon color="white">mdi-store</v-icon>
                  </v-btn>
                </v-row>
                <v-autocomplete 
                  v-else
                  :items="userStores"
                  background-color="input"
                  class="mt-7 ma-0"
                  dense
                  item-text="store_name"
                  item-id="store_party_id"
                  label="Store"
                  :menu-props="{ maxHeight: 210 }"
                  outlined
                  return-object
                  @change="getStoreBatches"
                  v-model="store">
                </v-autocomplete>
              </v-col>
            </v-row>
            <v-col mx="0" md="2" lg="2" xl="4" class="pl-0 pr-2" style="max-width: 255px">
              <v-menu
                :close-on-content-click="false"
                :nudge-left="5"
                bottom
                min-width="auto"
                offset-y
                ref="menu"
                transition="scale-y-transition"
                v-model="menu">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :label="batchDateLabel"
                    :value="dateText"
                    @click:clear="clearBatchDate"
                    clearable
                    background-color="input"
                    dense
                    readonly
                    hide-details=""
                    outlined
                    prepend-inner-icon="mdi-calendar-range"
                    v-bind="attrs"
                    v-on="on" />
                </template>
                <v-date-picker v-if="!isTprProRetailUser"
                  :show-current="false"
                  :max="batchEndDate !== batchDate ? batchEndDate : null"
                  @change="getBatches"
                  no-title
                  v-model="batchDate">
                  <v-spacer />
                  <v-btn :class="body" text @click="batchDate = ''" @blur="getBatches">
                    Clear
                  </v-btn>
                </v-date-picker>
                <v-date-picker v-else
                  :show-current="false"
                  :max="batchEndDate !== batchDate ? batchEndDate : null"
                  @change="getStoreBatches"
                  no-title
                  v-model="batchDate">
                  <v-spacer />
                  <v-btn :class="body" text @click="batchDate = ''" @blur="getStoreBatches">
                    Clear
                  </v-btn>
                </v-date-picker>
              </v-menu>
            </v-col>
            <v-col ml="5" md="2" lg="2" xl="4" class="pl-0 pr-2" style="max-width: 255px"  v-if="tprDateAsRange">
              <v-menu
                :close-on-content-click="false"
                :nudge-left="5"
                bottom
                min-width="auto"
                offset-y
                ref="endMenu"
                transition="scale-y-transition"
                v-model="endMenu">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    label="Batch End Date"
                    :value=endDateText
                    @click:clear="clearEndDate"
                    clearable
                    readonly
                    background-color="input"
                    dense
                    hide-details=""
                    outlined
                    prepend-inner-icon="mdi-calendar-range"
                    v-bind="attrs"
                    v-on="on" />
                </template>
                <v-date-picker
                  :min="batchDate"
                  :show-current="false"
                  @change="!isTprProRetailUser ? getBatches : getStoreBatches"
                  no-title
                  v-model="batchEndDate">
                  <v-spacer />
                  <v-btn :class="body" text @click="clearEndText()">
                    Clear
                  </v-btn>
                </v-date-picker>
              </v-menu>
            </v-col>
            <v-spacer />
            <v-menu
              v-if="isFiltered"
              bottom
              offset-y
              open-on-hover
              max-height="300">
              <template v-slot:activator="{ on }">
                <v-chip v-on="on" class="orange white--text ml-2 d-flex align-center" style="max-width: 100%; overflow: visible; flex-wrap: nowrap;">
                  <v-icon color="white" class="mr-2" small>
                    mdi-filter-outline
                  </v-icon>
                  <span class="text-capitalize text-truncate" style="flex-grow: 1; min-width: 0;">
                    Results for {{ searchValues.length }} search values
                  </span>
                  <v-tooltip top>
                    <template #activator="{ on }">
                      <v-btn
                        v-on="on"
                        @click="searchValues = []"
                        class="white--text ml-2"
                        icon
                        x-small>
                        <v-icon small>mdi-close-circle</v-icon>
                      </v-btn>
                    </template>
                    <span>Clear Filter</span>
                  </v-tooltip>
                </v-chip>
              </template>
              <v-card>
                <v-card-title class="text-subtitle-2 pb-0">
                  Search Values:
                </v-card-title>
                <v-list>
                  <v-list-item
                    v-for="(value, index) in searchValues"
                    :key="index">
                    {{ value }}
                  </v-list-item>
                </v-list>
              </v-card>
            </v-menu>
            <v-spacer />
            <TprCalendar></TprCalendar>
            <v-menu 
              :close-on-content-click="false"
              :nudge-bottom="3"
              :nudge-left="40"
              :value="scheduleMenu"
              bottom
              offset-y>
              <template #activator="{ on: menu, attrs }">
                <v-tooltip top>
                  <template #activator="{ on: tooltip }">
                    <v-btn v-if="canScheduleTPRBatches && !isReadOnlyAdmin"
                      small
                      :class="body"
                      @click="scheduleMenu = true"
                      class="grey lighten-2 ml-2"
                      height="38"
                      v-bind="attrs"
                      v-on="{ ...tooltip, ...menu }">
                      <v-icon>mdi-folder-clock-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Schedule Batch Generation</span>
                </v-tooltip>
              </template>
              <v-card class="fill">
                <v-card-title class="pb-0 text-subtitle-1">
                  Schedule TPR Batch Generation
                </v-card-title>
                <v-form ref="s_form" class="fill">
                  <v-autocomplete
                    :rules="[v => !!v || 'Batch Type Required', !batchAlreadyScheduled() || 'Cannot schedule this batch type again today']"
                    :items="batchTypes"
                    class="pt-4 px-4"
                    background-color="input"
                    outlined
                    dense
                    clearable
                    label="Batch Type"
                    v-model="scheduleBatchType">
                  </v-autocomplete>
                  <v-container>
                    <v-row>
                      <v-col class="ma-0 pa-0">
                        <v-text-field
                        :rules="[v => !!v || 'Date Required']"
                        :value="scheduleDateText"
                        background-color="input"
                        class="px-4 pb-0"
                        clearable
                        readonly
                        dense
                        :label="batchDateLabel"
                        outlined
                        @click:clear="scheduleBatchDate = null"/>
                      </v-col>
                    </v-row>
                  </v-container>
                  <v-divider />
                  <v-date-picker
                    :allowed-dates="allowedScheduleDates"
                    :max="batchEndDate || null"
                    :show-current="true"
                    color="primary"
                    no-title
                    v-model="scheduleBatchDate"
                    width="326">
                  </v-date-picker>
                </v-form>
                <v-divider />
                <v-card-actions flat class="fill">
                  <v-spacer />
                  <v-btn :class="body" text @click="scheduleMenu = false">
                    Cancel
                  </v-btn>
                  <v-btn
                    :class="body"
                    class="green white--text"
                    @click="scheduleBatchCreate">
                    Schedule
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-menu>
            <ItemSearch v-if="!isTprProRetailUser" @search="searchValues = $event" @clear="getBatches()" />
            <ItemSearch v-else @search="searchValues = $event" @clear="getStoreBatches()" />
            <v-menu 
              :close-on-content-click="false"
              :nudge-bottom="3"
              :nudge-left="40"
              :value="createMenu"
              bottom
              offset-y>
              <template #activator="{ on: menu, attrs }">
                <v-tooltip top>
                  <template #activator="{ on: tooltip }">
                    <v-btn v-if="!isTprProRetailUser && !isReadOnlyAdmin"
                      small
                      :class="body"
                      @click="createMenu = true"
                      class="grey lighten-2 ml-2"
                      height="38"
                      v-bind="attrs"
                      v-on="{ ...tooltip, ...menu }">
                      <v-icon>mdi-folder-plus-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Create Batch</span>
                </v-tooltip>
              </template>
              <v-card class="fill">
                <v-card-title class="pb-0 text-subtitle-1">
                  Create TPR Batch
                </v-card-title>
                <v-form ref="form" class="fill">
                  <v-text-field
                    :rules="[v => !!v || 'Name Required']"
                    background-color="input"
                    class="pt-4 px-4"
                    clearable
                    dense
                    label="Batch Name"
                    outlined
                    v-model="batchName">
                  </v-text-field>
                  <v-autocomplete
                    v-if="showBatchType"
                    :rules="[v => !!v || 'Batch Type Required']"
                    :items="batchTypes"
                    class="px-4"
                    background-color="input"
                    outlined
                    dense
                    clearable
                    label="Batch Type"
                    v-model="batchType">
                  </v-autocomplete>
                  <v-container>
                    <v-row>
                      <v-col class="ma-0 pa-0">
                        <v-text-field
                        :rules="[v => !!v || 'Date Required']"
                        :value="dateText"
                        background-color="input"
                        class="px-4 pb-0"
                        clearable
                        readonly
                        dense
                        :label="batchDateLabel"
                        outlined
                        @click:clear="batchDate = null"/>
                      </v-col>
                      <v-col class="ma-0 pa-0" v-if="tprDateAsRange">
                        <v-text-field
                          :rules="[v => !!v || 'Date Required']"
                          :value="endDateText"
                          background-color="input"
                          class="px-4 pb-0"
                          clearable
                          readonly
                          dense
                          label="Batch End Date"
                          outlined
                          @click:clear="batchEndDate = null"/>
                      </v-col>
                    </v-row>
                  </v-container>
                  <v-divider />
                  <v-date-picker
                    :allowed-dates="allowedDates"
                    :max="batchEndDate || null"
                    :show-current="false"
                    color="primary"
                    no-title
                    v-model="batchDate"
                    width="326">
                  </v-date-picker>
                  <v-date-picker
                    v-if="tprDateAsRange"
                    :min="batchDate"
                    :allowed-dates="allowedEndDates"
                    :show-current="false"
                    color="primary"
                    no-title
                    v-model="batchEndDate"
                    width="326">
                  </v-date-picker>
                </v-form>
                <v-divider />
                <v-card-actions flat class="fill">
                  <v-spacer />
                  <v-btn :class="body" text @click="createMenu = false">
                    Cancel
                  </v-btn>
                  <v-btn
                    :class="body"
                    class="green white--text"
                    @click="batchCreate">
                    Create
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-menu>
            <v-col md="3" lg="3" xl="4" class="pl-2 pr-0">
              <v-text-field
                background-color="input"
                class="mt-7"
                dense
                label="Filter Batches"
                outlined
                prepend-inner-icon="mdi-filter-outline"
                v-model="search" />
            </v-col>
          </v-toolbar>
        <v-divider />
      </template>
      <template #[`item.item_count`]="{item}">
        <v-chip class="mr-1" small :color="item.item_count > 0 ? 'grey lighten-2' : 'grey'">
          <span
            style="
              display: inline-block;
              margin: 0 auto;
              width: 50px;
              text-align: center;
            ">
            <span>{{ item.item_count ? formatNumber(item.item_count) : '0'}}</span>
            </span>
        </v-chip>
      </template>
      <template #[`item.batch_type`]="{ item }">
        <v-chip 
        v-if="item.batch_type" class="mr-4" small dark :color="getColor(item.batch_type)">
          <span
            style="
              display: inline-block;
              margin: 0 auto;
              width: 120px;
              text-align: center;
            ">
            <span>{{ item.batch_type }}</span>
          </span>
        </v-chip>
      </template>
      <template #[`item.tpr_date`]="{ item }">
        <td class="noWrap">{{ formatDateDisplay(item.tpr_date) }}</td>
      </template>
      <template #[`item.tpr_end_date`]="{ item }">
        <td class="noWrap">{{ formatDateDisplay(item.tpr_end_date) }}</td>
      </template>
      <template #[`item.created_on`]="{ item }">
        <td class="noWrap">{{ formatDateDisplay(item.created_on) }}</td>
      </template>
      <template #[`item.updated_by`]="{ item }">
        <td class="noWrap">
          {{ item.updated_by !== 'None' ? item.updated_by : '---' }}
        </td>
      </template>
      <template #[`item.updated_on`]="{ item }">
        <td class="noWrap">
          {{ item.updated_on ? formatDateDisplay(item.updated_on) : '---' }}
        </td>
      </template>
      <template #[`item.publish_on`]="{ item }">
        <td class="noWrap">
          {{ item.publish_on ? formatDateDisplay(item.publish_on) : '---' }}
        </td>
      </template>
      <template #[`item.status`]="{ item }">
        <v-chip class="mr-4" small dark :color="getColor(item.status)">
          <span
            style="
              display: inline-block;
              margin: 0 auto;
              width: 90px;
              text-align: center;
            ">
            <span>{{ getStatus(item.status) }}</span>
          </span>
        </v-chip>
      </template>
      <template #[`item.actions`]="{ item }">
        <v-menu offset-y>
          <template #activator="{ on }">
            <v-btn :disabled="!isBatchAvailable.includes(item.status) || isSyncing" v-on="on" icon class="mr-4" @click="selectedBatch = item">
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item
              v-for="(action, index) in actions"
              :key="index"
              link
              @click="action.method(item, store)">
              <v-list-item-icon class="mr-4">
                <v-icon>{{ action.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-subtitle>{{ action.text }}</v-list-item-subtitle>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
      <template #[`item.batch_info`]="{ item }">
        <v-menu
          :close-on-content-click="false"
          :nudge-left="50"
          offset-x
          offset-y
          open-on-hover>
          <template #activator="{ on }">
            <v-btn
              color="primary"
              icon
              tabindex="-1"
              v-on="on"
              @click.stop="copyId(item)">
              <v-icon>mdi-information-variant</v-icon>
            </v-btn>
          </template>
          <v-card>
            <v-list>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle>Batch ID</v-list-item-subtitle>
                  <v-list-item-title v-if="!isTprProRetailUser">{{ item.id }}</v-list-item-title>
                  <v-list-item-title v-else>{{ item.tpr_batch_id }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>
      </template>
      <template v-slot:footer>
        <v-divider></v-divider>
        <v-toolbar flat color="#fff" style="margin-top: 8px">
          <v-row align="center" style="padding-bottom: 3px">
            <v-spacer />
            <v-col cols="2" style="max-width: 150px">
              <v-select
                :items="pageSizes"
                :menu-props="{
                  top: true,
                  offsetY: true,
                  maxHeight: 500
                }"
                class="mt-7"
                dense
                label="Items Per Page"
                v-model="pageSize">
              </v-select>
            </v-col>
            <v-btn
              :disabled="disablePrevious"
              @click="previous"
              class="mx-2"
              icon
              large>
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <small>Page {{ page }}</small>
            <v-btn
              :disabled="disableNext"
              @click="next"
              class="mx-2"
              icon
              large>
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </v-row>
        </v-toolbar>
      </template>
    </v-data-table>
    <StoreGroupModal
      :value.sync="storeGroupModal"
      :storeParty="storeParty">
    </StoreGroupModal>
    <AlertDialog
      :value.sync="alertDialog"
      :batch="selectedBatch"
      :total="total"
      @update="batchUpdate($event)" />
    <EditNameDialog
      :value.sync="editNameDialog"
      :batch="selectedBatch"
      @close="closeEditNameModal()" 
      @update="getBatches()" />
    <DeleteBatchDialog
      :value.sync="deleteBatchDialog"
      :batch="selectedBatch"
      @close="closeDeleteBatchModal()" 
      @update="getBatches()" />
    <v-overlay absolute :value="creating || exporting">
      <v-progress-circular indeterminate size="100">
        {{ creating ? 'Creating...' : exporting ? 'Exporting...' : '' }}
      </v-progress-circular>
    </v-overlay>
  </v-container>
</template>
<script>
import Batches from '@/axios/batches'
import ItemSearch from '@/components/ItemSearch'
import TprCalendar from '@/components/TprCalendar.vue'
import StorePartiesDropdown from '@/components/StorePartiesDropdown.vue'
import StoreGroupModal from '@/components/StoreGroupModal.vue'
import { fullwidth } from '@/mixins/fullwidth'
import { paging } from '@/mixins/paging'
import { displayHelpers } from '@/mixins/displayHelpers'
import { userAccess } from '@/mixins/user-access'
import { utils } from '@/mixins/utils'
import { shared } from '@/mixins/shared'
export default {
  name: 'BatchList',
  components: { ItemSearch, TprCalendar, StorePartiesDropdown, StoreGroupModal },
  mixins: [fullwidth, displayHelpers, paging, userAccess, utils, shared],
  data() {
    return {
      adGroup: {},
      batchDate: null,
      batchEndDate: null,
      batches: [],
      batchName: null,
      batchType: null,
      scheduleBatchType: null,
      scheduleBatchDate: null,
      storeGroupModal: false,
      createMenu: false,
      scheduleMenu: false,
      creating: false,
      endMenu: false,
      interval: null,
      loading: false,
      menu: false,
      search: '',
      searchValues: [],
      selectedBatch: {},
      userStores: [],
      userStoreParties: [],
      store: {},
      storeParty: {},
      userGroupsLoading: true,
      batchTypes: ["NON PERISHABLE", "PERISHABLE"],
      scheduledBatches: [],
      publishInterval: 11
    }
  },
  computed: {
    headers() {
      let localHeaders = [
        { sortable: false, class: 'accent white--text', width: '2%' },
        { text: 'Batch Name', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'batch_name' },
        { text: '# of Items', align: 'center', sortable: true, filterable: false, class: 'accent white--text', value: 'item_count' },
        { text: 'Batch Type', align: 'center', sortable: true, filterable: true, class: 'accent white--text', value: 'batch_type' },
        { text: this.tprDateAsRange ? 'TPR Start Date' : 'TPR Date', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'tpr_date' },
        { text: 'TPR End Date', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'tpr_end_date' },
        { text: 'Due By', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'publish_on' },
        { text: 'Created By', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'created_by' },
        { text: 'Created On', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'created_on' },
        { text: 'Updated By', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'updated_by' },
        { text: 'Updated On', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'updated_on' },
        { text: 'Status', align: 'center', sortable: true, filterable: true, class: 'accent white--text', value: 'status' },
        { text: 'Actions', align: 'center', sortable: false, filterable: false, class: 'accent white--text', value: 'actions' },
        { align: 'center', sortable: false, filterable: false, class: 'accent white--text', value: 'batch_info' }
      ].filter(h => h.show === undefined || h.show)
      if (!this.tprDateAsRange) {
        localHeaders = localHeaders.filter(header => header.value !== 'tpr_end_date')
      }
      if (this.$auth.tenant != 'alliance-retail-group') {
        localHeaders = localHeaders.filter(header => !['item_count', 'batch_type', 'publish_on'].includes(header.value))
      }
      if (this.$auth.tenant === 'alliance-retail-group' && !this.isTprProRetailUser) {
        localHeaders = localHeaders.filter(header => header.value !== 'publish_on')
      }
      return localHeaders
    },
    isTprProRetailUser() {
      if (this.adminUser) return this.viewMode === 'STORE'
      return this.userGroups.includes('tpr-manager.pro') || this.userGroups.includes('tpr-manager.retailer')
    },
    useStoreParties() {
      return this.isTprProRetailUser && this.$auth.tenant === 'alliance-retail-group'
    },
    isTprAdmin() {
      return this.userGroups.includes('tpr-manager.admin')
    },
    canScheduleTPRBatches() {
      return this.isTprAdmin && ['alliance-retail-group'].includes(this.$auth.tenant)
    },
    tprDateAsRange() {
      return ['pricechopper'].includes(this.$auth.tenant)
    },
    showBatchType() {
      if(!this.isTprProRetailUser){
        return this.$auth.tenant === 'alliance-retail-group'
      }
      return false
    },
    shouldPoll() {
      return this.batches.some(
        batch => batch.status === 'PENDING' || batch.status === 'RUNNING'
      )
    },
    isFiltered() {
      return this.searchValues.length > 0
    },
    dateText() {
      return this.batchDate
        ? this.moment(this.batchDate).format(this.$dateConfig.date_display)
        : ''
    },
    scheduleDateText() {
      return this.scheduleBatchDate
        ? this.moment(this.scheduleBatchDate).format(this.$dateConfig.date_display)
        : ''
    },
    endDateText() {      
      return this.batchEndDate
        ? this.moment(this.batchEndDate).format(this.$dateConfig.date_display)
        : ''
    },
    batchDateLabel() {
      if (this.tprDateAsRange) return 'Batch Start Date'
      return "Batch Date"
    },
    userEmail() {
      return this.$auth.user?.email || 'guest'
    },
    allHeaders() {
      const userEmail = this.userEmail
      const allUserHeaders = JSON.parse(localStorage.getItem("userHeaders")) || {}
      const savedHeaders = allUserHeaders[userEmail] || []
      let headers = []
      if (this.$auth.tenant === 'alliance-retail-group') {
        headers =  [
          { sortable: false, class: 'accent', value: 'data-table-select', text: 'Checkbox' },
          { text: 'Department', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'department' },
          { text: 'Included', align: 'start', sortable: false, filterable: true, class: 'accent white--text', value: 'excluded' },
          { text: 'Rules Applied', align: 'start', sortable: false, filterable: true, class: 'accent white--text', value: 'rules_applied' },
          { text: 'Rules Promotion Price', align: 'start', sortable: false, filterable: true, class: 'accent white--text', value: 'rules_promotion_price' },
          { text: 'Original Promotion Price', align: 'start', sortable: false, filterable: true, class: 'accent white--text', value: 'original_promotion_price' },
          { text: 'Category', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'category' },
          { text: 'UPC', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'upc' },
          { text: 'Item ID', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'item_id' },
          { text: 'Description', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'description' },
          { text: 'Vendor ID', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'vendor_id' },
          { text: 'Vendor Name', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'vendor_name' },
          { text: 'Pack', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'qty_per_case' },
          { text: 'Size', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'pack_size' },
          { text: 'Case Cost/UP', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'case_freight' },
          { text: 'Case Cost', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'cost' },
          { text: 'Reg Unit Cost/UP', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'unit_cost' },
          { text: 'Reg Retail', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'retail_price' },
          { text: 'Reg GP%', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'reg_gross_profit_percent' },
          { text: 'AMAP', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'amap_allowance' },
          { text: 'DSD OI', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'dsd_oi_allowance' },
          { text: 'EBA', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'early_buy_allowance' },
          { text: 'TPR Billback', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'tpr_billback_allowance' },
          { text: 'TPR Scan', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'tpr_scan_allowance' },
          { text: 'EDLC Billback', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'edlc_billback_allowance' },
          { text: 'EDLC Scan', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'edlc_scan_allowance' },
          { text: 'Net Unit Cost/UP', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'net_unit_cost' },
          { text: 'Net GP%', align: 'start', sortable: false, filterable: false, class: 'accent white--text', value: 'gross_profit_percent' },
          { text: 'Compliance Enforced', align: 'center', sortable: true, class: 'accent white--text', value: 'enforce_compliance' },
          { text: 'Multiplier @ SRP', align: 'center', sortable: true, filterable: true, class: 'accent white--text', value: 'promotion_price', width: '250', disabled: false },
          { text: 'Linked Group', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'linked_group' },
          { text: 'Order Book Index', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'obxi' },
          { text: 'Like Code', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'like_code' },
          { text: 'Start Date', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'start_dt' },
          { text: 'End Date', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'end_dt' },
          { text: 'Promo Info', align: 'start', sortable: false, filterable: false, class: 'accent white--text', value: 'promo_info' },
          { text: 'Components', align: 'start', sortable: false, filterable: false, class: 'accent white--text', value: 'is_shipper' }
        ]
      } else if (this.$auth.tenant == 'awg') {
        headers = [
          { sortable: false, class: 'accent', value: 'data-table-select', text: 'Checkbox' },
          { text: 'Department', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'department' },
          { text: 'Department #', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'department_number' },
          { text: 'Rules Applied', align: 'start', sortable: false, filterable: true, class: 'accent white--text', value: 'rules_applied' },
          { text: 'Included', align: 'start', sortable: false, filterable: true, class: 'accent white--text', value: 'excluded' },
          { text: 'Category #', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'category_number' },
          { text: 'Category Description', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'category' },
          { text: 'Sub-Category #', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'subcategory_number' },
          { text: 'Sub-Category Description', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'subcategory' },
          { text: 'Linked Group', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'linked_group' },
          { text: 'UPC', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'upc' },
          { text: 'Item #', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'item_id' },
          { text: 'Item Description', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'item_description' },
          { text: 'Pack', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'qty_per_case' },
          { text: 'Size', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'pack_size' },
          { text: 'Case Cost', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'cost' },
          { text: 'Reg Unit Cost', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'unit_cost' },
          { text: 'Promo Info', align: 'start', sortable: false, filterable: false, class: 'accent white--text', value: 'promo_info' }, // magnifying glass
          { text: 'AMAP', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'amap_allowance' },
          { text: 'TPR Billback', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'tpr_billback_allowance' },
          { text: 'TPR Scan', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'tpr_scan_allowance' },
          { text: 'EDLC Billback', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'edlc_billback_allowance' },
          { text: 'EDLC Scan', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'edlc_scan_allowance' },
          { text: 'Total Allowance Per Unit', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'total_allowance_per_unit' },
          { text: 'Net Unit Cost', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'net_unit_cost' },
          { text: 'Compliance Enforced', align: 'center', sortable: true, filterable: true, class: 'accent white--text', value: 'enforce_compliance' },
          { text: 'Multiplier @ SRP', align: 'center', sortable: true, filterable: true, class: 'accent white--text', value: 'promotion_price', width: '250', disabled: false },
          { text: 'TPR GP%', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'gross_profit_percent' },
          { text: 'SRP Needed to Maintain Margin', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'srp_needed_to_maintain_margin' },
          { text: 'Difference to Regular Price', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'difference_to_regular_price' },
          { text: 'Current Retail Price', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'retail_price' },
          { text: 'Start Date', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'start_dt' },
          { text: 'End Date', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'end_dt' },
          { text: 'Components', align: 'start', sortable: false, filterable: false, class: 'accent white--text', value: 'is_shipper' },
          { text: 'Length of TPR', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'length_of_tpr' },
          { text: 'Previous TPR - Cost', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'previous_tpr_cost' },
          { text: 'Previous TPR - MSU', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'previous_tpr_msu' },
          { text: 'Previous TPR - Retail', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'previous_tpr_retail' },
          { text: 'Previous TPR - Margin', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'previous_tpr_margin' },
          { text: 'Current TPR - MSU', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'current_tpr_msu' },
          { text: 'Current TPR - Retail', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'current_tpr_retail' },
          { text: 'Current TPR - End Date', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'current_tpr_end_date' }
        ]
      } else {
        headers = [
          { sortable: false, class: 'accent', value: 'data-table-select', text: 'Checkbox' },
          { text: 'Department', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'department' },
          { text: 'Included', align: 'start', sortable: false, filterable: true, class: 'accent white--text', value: 'excluded' },
          { text: 'Rules Applied', align: 'start', sortable: false, filterable: true, class: 'accent white--text', value: 'rules_applied' },
          { text: 'Rules Promotion Price', align: 'start', sortable: false, filterable: true, class: 'accent white--text', value: 'rules_promotion_price' },
          { text: 'Original Promotion Price', align: 'start', sortable: false, filterable: true, class: 'accent white--text', value: 'original_promotion_price' },
          { text: 'Category', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'category' },
          { text: 'Linked Group', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'linked_group' },
          { text: 'UPC', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'upc' },
          { text: 'Item ID', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'item_id' },
          { text: 'Description', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'description' },
          { text: 'Vendor ID', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'vendor_id' },
          { text: 'Pack', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'qty_per_case' },
          { text: 'Size', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'pack_size' },
          { text: 'Case Cost', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'cost' },
          { text: 'Case Freight', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'case_freight' },
          { text: 'Reg Unit Cost', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'unit_cost' },
          { text: 'AMAP', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'amap_allowance' },
          { text: 'EBA', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'early_buy_allowance' },
          { text: 'TPR Billback', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'tpr_billback_allowance' },
          { text: 'TPR Scan', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'tpr_scan_allowance' },
          { text: 'EDLC Billback', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'edlc_billback_allowance' },
          { text: 'EDLC Scan', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'edlc_scan_allowance' },
          { text: 'Net Unit Cost', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'net_unit_cost' },
          { text: 'Compliance Enforced', align: 'center', sortable: true, class: 'accent white--text', value: 'enforce_compliance' },
          { text: 'Multiplier @ SRP', align: 'center', sortable: true, filterable: true, class: 'accent white--text', value: 'promotion_price', width: '250', disabled: false },
          { text: 'GP%', align: 'start', sortable: false, filterable: false, class: 'accent white--text', value: 'gross_profit_percent' },
          { text: 'Retail Price', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'retail_price' },
          { text: 'Order Book Index', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'obxi' },
          { text: 'Like Code', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'like_code' },
          { text: 'Start Date', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'start_dt' },
          { text: 'End Date', align: 'start', sortable: true, filterable: true, class: 'accent white--text', value: 'end_dt' },
          { text: 'Promo Info', align: 'start', sortable: false, filterable: false, class: 'accent white--text', value: 'promo_info' },
          { text: 'Components', align: 'start', sortable: false, filterable: false, class: 'accent white--text', value: 'is_shipper' }
        ]
      }
      if (!this.tprProTenants) {
        headers = headers.filter(header =>
          !['original_promotion_price', 'rules_promotion_price', 'rules_applied', 'excluded', 'obxi', 'like_code'].includes(header.value)
        )
      }
      if (this.isTprProRetailUser) {
        headers = headers.filter(header =>
          !['rules_promotion_price', 'promo_info'].includes(header.value)
        )
      }
      if (savedHeaders.length > 0) {
        return headers
          .filter(header => savedHeaders.includes(header.value))
          .sort((a, b) => savedHeaders.indexOf(a.value) - savedHeaders.indexOf(b.value))
      }
      return headers
    },
    disablePrevious() {
      return (this.page - 1) * this.pageSize === 0
    },
    disableNext() {
      return this.batches.length < this.pageSize
    }
  },
  watch: {
    userAdGroups: {
      handler(newValue) {
        if (newValue?.length > 0 && !sessionStorage.tpr_manager_ad_group) {
          let index = 0
          if (this.$auth.tenant == 'pricechopper') {
            index = newValue.findIndex(newValue => newValue.name === 'PCE Ad Group')
          }
          this.adGroup = newValue[index]
        }
      },
      deep: true
    },
    userStores: {
      handler(newValue) {
        if( newValue?.length > 0 && !sessionStorage.tpr_pro_store){
          let index = 0
          this.store = newValue[index]
        }
      },
      deep: true
    },
    adGroup: {
      handler(newValue) {
        if (newValue && !this.isTprProRetailUser) {
          sessionStorage.setItem(
            'tpr_manager_ad_group',
            JSON.stringify(newValue)
          )
          this.userGroupsLoading = false
          this.getBatches()
        }
      },
      deep: true
    },
    store: {
      handler(newValue) {
        if(newValue) {
          sessionStorage.setItem('tpr_pro_store', JSON.stringify(newValue))
          this.getStoreBatches()
        }
      },
      deep: true
    },
    batchDate: {
      handler(newValue) {
        if (newValue) {
          this.menu = false
          sessionStorage.setItem('tpr_manager_tpr_date', newValue)
        } else {
          sessionStorage.removeItem('tpr_manager_tpr_date')
        }
        this.page = 1
      }
    },
    batchEndDate: {
      handler(newValue) {
        if (newValue) {
          this.endMenu = false
          sessionStorage.setItem('tpr_manager_tpr_end_date', newValue)
        } else {
          sessionStorage.removeItem('tpr_manager_tpr_end_date')
        }
        this.page = 1
      }
    },
    searchValues: {
      handler(newValue) {
        if (newValue) {
          sessionStorage.setItem(
            'tpr_manager_batch_item_search',
            JSON.stringify(newValue)
          )
          if(!this.isTprProRetailUser) {
            this.getBatches()
          } else {
            this.getStoreBatches()
          }
        }
      }
    },
    shouldPoll: {
      handler(newValue) {
        if (newValue === true) {
          clearInterval(this.interval)
          if (!this.isTprProRetailUser) {
            this.interval = setInterval(() => {
              this.getBatches()
            }, 30000)
          } else {
            this.interval = setInterval(() => {
              this.getStoreBatches()
            }, 30000)
          }
        } else {
          clearInterval(this.interval)
          this.interval = null
        }
      }
    },
    async viewMode() {
      if (this.$auth.tenant !== 'alliance-retail-group') return
      if (this.isTprProRetailUser) {
        await this.getStores()
        if (this.useStoreParties) {
          await this.getUserStoreGroups()
          this.getStoreBatches()
        } else if (!this.store?.store_party_id && this.userStores.length > 0) {
          this.store = this.userStores[0]
        } else {
          this.getStoreBatches()
        }
      } else if (!this.adGroup?.id && this.userAdGroups.length > 0) {
        this.adGroup = this.userAdGroups[0]
      } else {
        this.getBatches()
      }
    },
  },
  beforeDestroy() {
    clearInterval(this.interval)
  },
  async created() {
    this.batches = []
    // clear user-specific session storage if the user's email changed
    const currentUser = this.$auth.user?.email
    const storedUser = sessionStorage.getItem('tpr_manager_current_user')
    if (storedUser && storedUser !== currentUser) {
      sessionStorage.removeItem('tpr_manager_ad_group')
      sessionStorage.removeItem('tpr_pro_store')
      sessionStorage.removeItem('tpr_pro_store_group')
      sessionStorage.removeItem('tpr_manager_tpr_date')
      sessionStorage.removeItem('tpr_manager_tpr_end_date')
      sessionStorage.removeItem('tpr_manager_batch_item_search')
    }
    if (currentUser) {
      sessionStorage.setItem('tpr_manager_current_user', currentUser)
    }
    if(this.isTprProRetailUser){
      if(sessionStorage.tpr_pro_store) this.store = JSON.parse(sessionStorage.tpr_pro_store)
      if (sessionStorage.tpr_manager_tpr_date) {
        this.batchDate = sessionStorage.tpr_manager_tpr_date
      } else {
        this.batchDate = ''
      }
      if (this.tprDateAsRange) { 
        if (sessionStorage.tpr_manager_tpr_end_date) {
          this.batchEndDate = sessionStorage.tpr_manager_tpr_end_date
        }
      }
      if (sessionStorage.tpr_manager_batch_item_search) {
        this.searchValues = JSON.parse(
          sessionStorage.tpr_manager_batch_item_search
        )
      }
      this.tableChanged = this.tableChange(this.getStoreBatches)
      this.nonTableHeight = 240
      await this.getStores()
      if (this.useStoreParties) await this.getUserStoreGroups()
      await this.getStoreBatches()
    } else {
        if (sessionStorage.tpr_manager_ad_group)
        this.adGroup = JSON.parse(sessionStorage.tpr_manager_ad_group)
        if (sessionStorage.tpr_manager_tpr_date) {
        this.batchDate = sessionStorage.tpr_manager_tpr_date
      } else {
        this.batchDate = ''
      }
      if (this.tprDateAsRange) {
        if (sessionStorage.tpr_manager_tpr_end_date) {
          this.batchEndDate = sessionStorage.tpr_manager_tpr_end_date
        }
      }
      if (sessionStorage.tpr_manager_batch_item_search) {
        this.searchValues = JSON.parse(
          sessionStorage.tpr_manager_batch_item_search
        )
      }
      if (this.canScheduleTPRBatches) {
        this.getScheduledBatches()
      }
      this.tableChanged = this.tableChange(this.getBatches)
    }
    sessionStorage.removeItem('tpr_manager_selected_batch')
    sessionStorage.removeItem('tpr_manager_tpr_date')
    sessionStorage.removeItem('tpr_manager_tpr_end_date')
    sessionStorage.removeItem('tpr_manager_item_search')
    this.nonTableHeight = 240
  },
  methods: {
    copyId(batch) {
      const batch_id = !this.isTprProRetailUser ? batch.id : batch.tpr_batch_id
      navigator.clipboard.writeText(batch_id)
      this.notify('Copied')
    },
    allowedDates(value) {
      if (this.tprDateAsRange) {
        return value
      }
      if (this.$auth.tenant === 'alliance-retail-group') {
        // Don't allow any dates until batch type is selected
        if (this.batchType == null)
          return ''
        // Filter batches down to ones with currently selected batch type, and then check if any dates conflict
        if (this.batches.filter(batch => batch.batch_type === this.batchType).map(batch => batch.tpr_date).includes(value)) {
          return ''
        }
      }
      else if (this.batches.map(batch => batch.tpr_date).includes(value)) {
        return ''
      }
      const availableDate = new Date(value)
      if (this.$auth.tenant === 'awg') {
        return availableDate.getDay() === 0 ? value : ''  // Monday
      }
      return availableDate.getDay() === 6 ? value : ''  // Sunday
    },
    allowedEndDates(value) {
      if (!this.batches.map(batch => batch.tpr_date).includes(this.batchDate)) {
        return value
      } else if (!this.batches.map(batch => batch.tpr_end_date).includes(value)) {
        return value
      }
      return ''
    },
    batchAlreadyScheduled () {
      const today = new Date().toISOString().split('T')[0]
      const madeToday = this.scheduledBatches.filter(batch => batch.generate_date == today && batch.batch_type == this.scheduleBatchType).length
      return madeToday > 0
    },
    allowedScheduleDates(value) {
      if (this.$auth.tenant == 'alliance-retail-group') {
        if (this.scheduleBatchType == null)
          return ''
        if (this.scheduledBatches.filter(batch => batch.batch_type == this.scheduleBatchType).map(batch => batch.tpr_date).includes(value))
          return ''
        // Look forward 3 WEEKS for Perishable, 4 for Non Perishable - From the pervious Sunday
        // Offset by 1 day to account for javascript default timezone when creating dates
        const lookForward = ((this.scheduleBatchType == 'PERISHABLE' ? 3 : 4) * 7) - 1
        const today = new Date()
        let availableAfter = new Date(today.setDate(today.getDate() - today.getDay() + lookForward))
        availableAfter.setHours(0, 0, 0, 0)
        const availableDate = new Date(value)
        if (availableDate < availableAfter) {
          return ''
        }
        return availableDate.getDay() === 6 ? value : ''  // Sundays
      }      
    },
    getColor(status) {
      switch (status) {
        case 'PENDING':
          return 'grey darken-1'
        case 'RUNNING':
          return 'amber darken-1'
        case 'COMPLETE':
          return 'green'
        case 'FAILED':
          return 'red'
        case 'PUBLISHED':
          return 'primary'
        case 'NON PERISHABLE':
          return 'primary'
        case 'PERISHABLE':
          return 'amber darken-3'
        default:
          return
      }
    },
    async getStores() {
      try {
        this.userStores = await this.$store.dispatch('getUserProfileTprStoreGroups')
      } catch (err) {
        console.log('Get stores Error: ', err)
      }
    },
    async getUserStoreGroups() {
      if (this.userStoreParties.length === 0) {
        try {
          const data = await this.$store.dispatch('getUserProfileTprStoreGroups')
          const storeGroups = data.filter(p => p.party_type === 'TPR_STORE_GROUP').sort((a, b) => {return a.store_name.localeCompare(b.store_name)})
          const stores = data.filter(p => p.party_type === 'STORE').sort((a, b) => {return a.store_name.localeCompare(b.store_name)})
          this.userStoreParties = storeGroups.length > 0 ? [...[{header: 'Store Groups'}, ...storeGroups], ...[{header: 'Stores'}, ...stores]] : [...stores]
          const stored = sessionStorage?.tpr_pro_store_group ? JSON.parse(sessionStorage.tpr_pro_store_group) : null
          const storeParty = stored?.store_party_id ? stored : this.userStoreParties.find(p => p?.store_party_id)
          this.updateStoreParty(storeParty)
        } catch (err) {
          console.error(err?.response?.data?.message)
        }
      }
    },
    async getStoreBatches() {
      const shouldFetchBatches = (this.useStoreParties && this.storeParty.store_party_id) || (this.store.store_party_id && !this.useStoreParties)
      if (shouldFetchBatches) {
        if (!this.shouldPoll) {
          this.loading = true
          this.batches = []
        }
        this.startDt = null
        this.endDt = null
        if (this.batchDate) {
          this.startDt = this.batchDate
        }
        if (this.batchEndDate) {
          this.endDt = this.batchEndDate
        }
        try {
          let store_ids = []
          if (this.useStoreParties) store_ids = this.storeParty.children.length > 0 ? this.storeParty.children.map(child => child.store_party_id) : [this.storeParty.store_party_id]
          else store_ids = [this.store.store_party_id]
          const payload = {
            store_ids: store_ids,
            keys: this.searchValues,
            start_dt: this.startDt,
            end_dt: this.endDt
          }
          const { data } = await Batches.getStoreBatches(
            payload,
            this.pageSize,
            this.from
          )
          const accessibleStores = this.useStoreParties
            ? this.userStores.flatMap(s => s.party_type === 'TPR_STORE_GROUP' ? (s.children || []) : [s])
            : this.userStores
          for (const batch of data) {
            batch.publish_on = this.calculatePublishDate(batch)
            if (this.useStoreParties) {
              const store = accessibleStores.find(s => batch.store_id === s.store_party_id)
              if (store) batch.store_name = store.store_name
            }
          }
          this.batches = data
        } catch (err) {
          console.error(err)
          const message = err?.response?.data?.message || err.message
          this.alert(`Error Retrieving Batches: ${message}`)
        } finally {
          this.loading = false
        }
      }
    },
    async getBatches() {
      if (this.adGroup.id){
        if (!this.shouldPoll) {
          this.loading = true
          this.batches = []
        }
        this.startDt = null 
        this.endDt = null
        if (this.batchDate) {
          this.startDt = this.batchDate
        }
        if (this.batchEndDate){
          this.endDt = this.batchEndDate
        }
        try {
          const { data } = await Batches.getBatches(
            this.adGroup.id,
            this.startDt,
            this.endDt,
            this.pageSize,
            this.from,
            this.searchValues
          )
          this.batches = data
        } catch (err) {
          console.error(err)
          const message = err?.response?.data?.message || err.message
          this.alert(`Error Retrieving Batches: ${message}`)
        } finally {
          this.loading = false
        }
      }
    },
    async batchCreate() {
      const valid = this.$refs.form.validate()
      if (valid) {
        this.createMenu = false
        this.creating = true
        try {
          await Batches.batchCreate(
            this.adGroup.id,
            this.batchDate,
            this.batchName,
            this.batchEndDate,
            this.batchType
          )
          this.notify(`${this.batchName} Created Successfully`)
          this.$refs.form.reset()
        } catch (err) {
          console.error(err)
          const message = err?.response?.data?.message || err.message
          this.alert(`Error Creating ${this.batchName}: ${message}`)
        } finally {
          await this.getBatches()
          this.creating = false
        }
      }
    },
    async scheduleBatchCreate() {
      const valid = this.$refs.s_form.validate()
      if (valid) {
        this.scheduleMenu = false
        this.creating = true
        try {
          await Batches.scheduleBatchCreation(
            this.scheduleBatchType,
            this.scheduleBatchDate
          )
          this.notify(`Batch Creation Successfully scheduled for ${this.scheduleBatchDate}`)
          this.scheduleBatchDate = null
          this.scheduleBatchType = null
          this.getScheduledBatches()
        } catch (err) {
          console.error(err)
          const message = err?.response?.data?.message || err.message
          this.alert(`Error Scheduling Batch Creation: ${message}`)
        } finally {
          await this.getBatches()
          this.creating = false
        }
      }
    },
    viewReport(batch) {
      const params = {
        tpr_date: batch.tpr_date,
        tpr_end_date: batch.tpr_end_date,
        selected_batch: batch
      }
      if (this.isTprProRetailUser) {
        if (this.useStoreParties && this.storeParty?.party_type === 'TPR_STORE_GROUP') {
          const store = this.storeParty.children.find(p => p.store_party_id === batch.store_id)
          this.store = { ...store, store_id: store.store_party_id }
        } else if (this.useStoreParties && this.storeParty?.party_type === 'STORE') {
          this.store = { ...this.storeParty, store_id: this.storeParty.store_party_id }
        }
        params.store = this.store
        if (this.useStoreParties) { 
          params.store_party = this.storeParty
          params.user_stores = this.userStores
        }
      } else {
        params.ad_group = this.ad_group
      }
      if (this.isBatchAvailable.includes(batch.status)) {
        // Adjust query based on isTprProRetailUser
        const query = this.isTprProRetailUser
          ? { id: batch.tpr_batch_id }
          : { id: batch.id }
        this.$router.push({
          name: 'BatchView',
          params: params,
          query: query
        })
      } else {
        return
      }
    },
    clearBatchDate() {
      this.batchDate = ''
      this.menu = true
      if(!this.isTprProRetailUser){
        this.getBatches()
      } else {
        this.getStoreBatches()
      }
    },
    clearEndDate() {
      this.batchEndDate = ''
      this.endMenu = true
      if(!this.isTprProRetailUser){
        this.getBatches()
      } else {
        this.getStoreBatches()
      }
      return
    },
    async getScheduledBatches() {
      const result = await Batches.getScheduledBatches()
      this.scheduledBatches = result.data
    },
    getStatus(status) {
      // ARG Batches show REVIEW instead of COMPLETE
      if (this.$auth.tenant == 'alliance-retail-group' && status == 'COMPLETE') {
        return 'REVIEW'
      } else {
        return status
      }      
    },
    updateStoreParty(storeParty) {
      this.storeParty = storeParty || {}
      this.getStoreBatches()
    },
    calculatePublishDate(batch) {
      const tpr_date = this.moment(batch.tpr_date)
      let lookback = this.publishInterval
      // Price pro stores publish a week later than regular stores, so reduce the lookback by 7
      if (this.store.is_price_pro_store)
        lookback -= 7
      const publish_date = tpr_date.subtract(lookback, 'days')
      return publish_date.format('YYYY-MM-DD')
    },
    buildSearchExportVM() {
      let storeIds = []
      if (this.useStoreParties) {
        if (this.storeParty?.party_type === 'TPR_STORE_GROUP') {
          storeIds = (this.storeParty.children || []).map(
            store => store.store_party_id
          )
        } else if (this.storeParty?.store_party_id) {
          storeIds = [this.storeParty.store_party_id]
        }
      } else if (this.store?.store_party_id) {
        storeIds = [this.store.store_party_id]
      }
      return {
        tpr_date: this.selectedBatch.tpr_date,
        store_ids: storeIds
      }
    }
  }
}
</script>
<style>
.theme--light.v-data-table
  .v-data-table-header
  th.sortable
  .v-data-table-header__icon {
  color: #fff !important
}
</style>